import { useRef, useState } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router';
import { AppProvider } from '@/providers/AppProvider';
import { AppBootSkeleton } from '@/layout/AppLayoutSkeleton';
import { useAuthStore } from '@/stores/auth-store';
import { startRenewalSchedule } from '@/lib/auth/renewalSchedule';
import { useProfile } from '@/api/queries/useProfile';
import { queryClient } from '@/lib/query-client';
import { LogoutSplash, WorkspaceSwitchSplash } from '@/components/auth/LogoutSplash';

/**
 * One-way boot gate: shows the skeleton ONLY until its children render once
 * (cold load with a stored token must not run guards against an unresolved
 * profile). Never swaps mounted children back out — remounting the router
 * re-fires every mount effect and freezes match context, which once re-fired
 * the SSO callback exchange with a spent single-use OIDC code+state (login
 * loop, PR #377). Post-login `token && isLoading` windows are safe unmasked:
 * guards await the profile themselves. Structural on purpose: the component
 * that owns the RouterProvider has no non-router render path. The idempotent
 * ref write is StrictMode-safe.
 */
function BootGate({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  const { isLoading } = useProfile();

  const hasBooted = useRef(false);
  if (!hasBooted.current && token && isLoading) {
    return <AppBootSkeleton />;
  }
  hasBooted.current = true;

  return children;
}

function V2AppRouter() {
  const { token, slug, isSuperAdmin, isLoggingOut, isSwitchingWorkspace } = useAuthStore();
  const { data: user } = useProfile();

  return (
    <>
      <RouterProvider
        router={router}
        context={{
          auth: {
            isAuthenticated: !!token,
            isSuperAdmin: isSuperAdmin(),
            user,
            slug: slug(),
            accessToken: token
          },
          queryClient
        }}
      />
      {isLoggingOut && <LogoutSplash />}
      {isSwitchingWorkspace && <WorkspaceSwitchSplash />}
    </>
  );
}

export function V2App() {
  // useState initializer: loads the stored session synchronously BEFORE the
  // first render, so router guards never run against an empty auth store.
  // The renewal schedule starts right after, on the now-loaded session, so a
  // token that expires while the user reads a page is replaced before any
  // request fails. Idempotent, so StrictMode's double invoke is harmless.
  useState(() => {
    useAuthStore.getState().initFromStorage();
    startRenewalSchedule();
  });

  return (
    <AppProvider>
      <BootGate>
        <V2AppRouter />
      </BootGate>
    </AppProvider>
  );
}
